/**
 * Calendar Drag Controller — manages drag-to-reschedule operations
 * for cards on the calendar view.
 *
 * Follows the same stateless-controller pattern as createDragController
 * in useDragAndDrop.ts. React components wrap this with useState.
 */

import { computeRescheduleDates } from '../utils/date-utils';
import type { CardSummary } from '../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CalendarDragItem {
  readonly cardId: string;
  readonly originalDueDate: string;
  readonly originalStartDate: string | null;
}

export interface CalendarDropTarget {
  readonly date: string; // 'YYYY-MM-DD'
}

export interface CalendarDragState {
  readonly isDragging: boolean;
  readonly dragItem: CalendarDragItem | null;
  readonly dropTarget: CalendarDropTarget | null;
}

export interface CalendarDragCallbacks {
  onReschedule(
    cardId: string,
    newDueDate: string,
    newStartDate: string | null,
  ): Promise<{ success: boolean }>;
}

export interface CalendarDropResult {
  readonly success: boolean;
  readonly error?: string;
  readonly reverted: boolean;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL_STATE: CalendarDragState = {
  isDragging: false,
  dragItem: null,
  dropTarget: null,
};

// ---------------------------------------------------------------------------
// Controller factory
// ---------------------------------------------------------------------------

/**
 * Creates a calendar drag controller for date-based card rescheduling.
 *
 * Each method mutates internal state and returns the new state snapshot.
 * `drop` is async — it computes new dates via `computeRescheduleDates`,
 * invokes the callback, and reverts state if the callback indicates failure.
 */
export function createCalendarDragController(callbacks: CalendarDragCallbacks) {
  let state: CalendarDragState = { ...INITIAL_STATE };

  function startDrag(item: CalendarDragItem): CalendarDragState {
    state = { isDragging: true, dragItem: item, dropTarget: null };
    return state;
  }

  function updateTarget(target: CalendarDropTarget | null): CalendarDragState {
    state = { ...state, dropTarget: target };
    return state;
  }

  async function drop(): Promise<CalendarDropResult> {
    if (!state.dragItem || !state.dropTarget) {
      state = { ...INITIAL_STATE };
      return { success: false, error: 'No drag item or drop target', reverted: false };
    }

    const { dragItem, dropTarget } = state;

    // Build a minimal CardSummary for computeRescheduleDates.
    // Only dueDate and startDate are accessed by that function.
    const cardProxy = {
      cardId: dragItem.cardId,
      cardNumber: 0,
      title: '',
      listId: '',
      swimlaneId: '',
      sort: 0,
      labels: [] as readonly string[],
      members: [] as readonly { userId: string; displayName: string }[],
      memberAvatars: [] as readonly string[],
      dueDate: dragItem.originalDueDate,
      startDate: dragItem.originalStartDate,
      coverImage: null,
      checklistProgress: null,
      subtaskProgress: null,
      isSubtask: false,
      parentCardId: null,
      customFields: {},
    } satisfies CardSummary;

    const { dueDate: newDueDate, startDate: newStartDate } = computeRescheduleDates(
      cardProxy,
      dropTarget.date,
    );

    try {
      const result = await callbacks.onReschedule(dragItem.cardId, newDueDate, newStartDate);

      state = { ...INITIAL_STATE };

      if (!result.success) {
        return { success: false, error: 'Reschedule rejected', reverted: true };
      }

      return { success: true, reverted: false };
    } catch {
      state = { ...INITIAL_STATE };
      return { success: false, error: 'Reschedule failed', reverted: true };
    }
  }

  function cancel(): CalendarDragState {
    state = { ...INITIAL_STATE };
    return state;
  }

  function getState(): CalendarDragState {
    return state;
  }

  return { startDrag, updateTarget, drop, cancel, getState };
}
